/**
 * denialSurface.js — say "you cannot do this" where a page currently says "try again".
 *
 * ── WHAT THIS IS ACTUALLY FIXING ────────────────────────────────────────────────────
 *
 * Not "the call sites have no 403 handling". A large number have the WRONG branch's
 * handling. 21 route modules render `emptyState('error', 'Could not load … Please try
 * again.')` from the catch that a 403 falls into, so a permission denial is presented as a
 * transient failure with an instruction to retry — a loop the user cannot win.
 *
 * That is invisible today only because mvvLegit's interceptor signs the user out before
 * the catch ever renders. Narrowing it to 401 removes the preemption and turns those 21
 * slots into the visible 403 surface, still saying "try again". This module exists so
 * those catches can say the true thing instead.
 *
 * ── WHY THE STATUS HAS TO BE CARRIED ────────────────────────────────────────────────
 *
 * A catch cannot branch on a status it never received. The prevailing shape is
 * `if (!res.ok) throw new Error('HTTP ' + res.status)` — the status survives only as
 * prose — and plenty of sites drop it entirely (`throw new Error('Failed to load')`,
 * `throw new Error()`). So this module supplies the bridge: failIfNotOk() throws an
 * HttpError carrying the real status, and isDenial() reads it from either an HttpError or
 * a Response. Without that, "handle 403 in the catch" is not expressible at most sites.
 *
 * ── OPT-IN, AND NOT AS A PREFERENCE ─────────────────────────────────────────────────
 *
 * A denial renders where the caller says it renders, and nowhere by default. That is not
 * a cautious rollout choice; there is no seam for a default. A 403 arrives as an ordinary
 * Response at 152 different call sites, and rendering into the right place requires
 * knowing WHICH slot the request was filling. That is exactly the per-page knowledge this
 * module does not have and cannot infer. AuthController does wrap window.fetch, so a
 * central observer is technically possible — but it could only produce a page-level
 * banner, and a page-level surface is wrong here: ad-performance-route.js fills 13
 * distinct slots, dashboard 8, leads 6, so one dead card must not blank twelve live ones.
 *
 * The honest cost, stated rather than discovered: until a site opts in, its 403 keeps
 * rendering "Please try again". Opt-in leaves those 21 WRONG, not silent, which is why
 * they are the targeted follow-up rather than an open-ended rollout.
 *
 * ── RENDER OR REMOVE IS THE CALLER'S CHOICE ─────────────────────────────────────────
 *
 * Two outcomes, both first-class, and this module never picks between them. Sometimes the
 * right response to a denial is a message in the slot; sometimes it is the region ceasing
 * to exist, because announcing a feature the user does not have is worse than its absence
 * (settings-workspace-route.js removes its team card, settings-notifications-route.js
 * keeps its webhook card hidden). Only the caller knows whether a region is essential
 * content or an optional card. A surface that guessed would reproduce the exact
 * regression we declined to inflict on the seven sites that already handle 403.
 *
 * ── 403 ONLY ────────────────────────────────────────────────────────────────────────
 *
 * 500, network failures and 404 keep their existing paths untouched. "Something broke" is
 * transient and retry is the point; "this is gone" is a third fact again. Folding them in
 * would blur the distinction this module exists to draw. And a denial gets NO retry
 * affordance, for the same reason it gets different words.
 */

/** An HTTP failure that still knows its status. */
export class HttpError extends Error {
  constructor(status, message, body = null) {
    super(message || `HTTP ${status}`);
    this.name = 'HttpError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Throw an HttpError for a non-2xx, so the catch downstream can tell a denial from a
 * breakage. Returns the response untouched when it is ok, so it reads as a pass-through.
 */
export function failIfNotOk(res, body = null) {
  if (res && res.ok) return res;
  const status = res ? res.status : 0;
  const message = (body && (body.message || body.error)) || `HTTP ${status}`;
  throw new HttpError(status, message, body);
}

/**
 * Is this a denial? Accepts a Response, an HttpError, or anything with a numeric status,
 * so a call site can ask the question wherever it happens to be holding the failure.
 *
 * Deliberately narrow: 401 is NOT a denial. It means the session is gone and belongs to
 * the sign-out path, which is the whole distinction the server-side 401 work established.
 */
export function isDenial(x) {
  return !!x && typeof x === 'object' && x.status === 403;
}

/**
 * Markup for a denied region. Mirrors emptyState(icon, msg) so it drops straight into the
 * `slot.innerHTML = …` sites that already exist, at the same granularity they already use.
 *
 * No retry affordance, by design: retrying a permission denial never succeeds.
 */
export function denialState(message = 'You do not have access to this.', { compact = false } = {}) {
  const safe = String(message).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  // `compact` drops the lock glyph, for the case where ONE denied request feeds several
  // slots at once. dashboard-route.js's /dashboard/stats feeds six panels, and six lock
  // icons read as "you are locked out of the dashboard" rather than "this data is not
  // available to you" — an overstatement produced by repeating the glyph, not by the
  // scope, which is correct at six. The repeated message is true; the repeated icon is
  // what misleads. The KPI tiles in that same catch already degrade as plain text for
  // the same reason.
  const icon = compact ? '' : '<span class="material-symbols-outlined" aria-hidden="true">lock</span>';
  return `<div class="m5t-empty m5t-empty--denied${compact ? ' m5t-empty--denied-compact' : ''}">${icon}<p>${safe}</p></div>`;
}

/**
 * The other outcome: the region stops existing. For cards whose absence is the correct
 * answer, where a denial message would advertise a feature the viewer cannot use.
 * Tolerates a missing element so a caller need not guard the lookup.
 */
export function removeDenied(el) {
  const node = typeof el === 'string' ? document.getElementById(el) : el;
  node?.remove();
  return !!node;
}
